import { DomainResult, Factor, ProcessedResponse, Question, ResultQuestion } from "../../types";

function getPerformanceLevel(value: number) {
    let performanceLevel: "Strong" | "Good" | "Needs Work";
    let category: "Thriving" | "Stable" | "Needs Focus";
    if (value < 33.33) {
        performanceLevel = "Needs Work";
        category = "Needs Focus";
    } else if (value < 66.66) {
        performanceLevel = "Good";
        category = "Stable";
    } else {
        performanceLevel = "Strong";
        category = "Thriving";
    }
    return {
        performanceLevel,
        category
    }
}

// Transform response data to the expected format
export function transformResponsesToDomainResult(responses: ProcessedResponse[], questions: Question[]): DomainResult | null {
    if (!responses || responses.length === 0 || !questions) return null;

    const firstDomain = responses[0]?.domain_name;
    if (!firstDomain) return null;

    const domainResponses = responses.filter((r) => r.domain_name === firstDomain);

    // Group responses by factor name, ensuring no duplicates
    const factorGroups = domainResponses.reduce((acc, response) => {
        const factor = response.factor_name;
        if (!acc[factor]) acc[factor] = [];

        // Check if this question_id is already added for this factor
        const existingQuestion = acc[factor].find((r) => r.question_id === response.question_id);
        if (!existingQuestion) {
            acc[factor].push(response);
        }
        return acc;
    }, {} as Record<string, ProcessedResponse[]>);

    const factors: Factor[] = Object.entries(factorGroups).map(([factorName, factorResponses]) => {
        const avgValue = factorResponses.reduce((sum, r) => sum + r.value, 0) / factorResponses.length;

        const { performanceLevel, category } = getPerformanceLevel(avgValue);

        // Remove duplicate questions based on question_id
        const uniqueQuestions = factorResponses.reduce((acc, response) => {
            const existing = acc.find((q) => q.question_id === response.question_id);
            if (!existing) {
                acc.push(response);
            }
            return acc;
        }, [] as ProcessedResponse[]);

        const resultQuestions: ResultQuestion[] = uniqueQuestions.map((response) => {
            const surveyQuestion = questions?.find((q) => q.question_id === response.question_id);

            const { performanceLevel: questionPerformanceLevel, category: questionCategory } = getPerformanceLevel(response.value);

            return {
                id: `q${response.question_id}`,
                text: surveyQuestion?.question_title || `Question ${response.question_id}`,
                performanceLevel: questionPerformanceLevel,
                category: questionCategory,
                value: response.value,
            };
        });

        return {
            name: factorName,
            performanceLevel,
            category,
            questions: resultQuestions,
        };
    });

    const allValues = domainResponses.map((r) => r.value);
    const avgDomainValue = allValues.reduce((sum, val) => sum + val, 0) / allValues.length;

    const { performanceLevel: domainPerformanceLevel, category: domainCategory } = getPerformanceLevel(avgDomainValue);

    return {
        domain: firstDomain,
        performanceLevel: domainPerformanceLevel,
        category: domainCategory,
        factors,
    };
};

export const getDomainDescription = (domain: string) => {
    const descriptions = {
        Relationships:
            "Relationships at work are always voluntary. When you find meaningful work and a sense of connection in what you do, strong relationships grow naturally, creating “eustress”—positive, performance-based stress that reduces personal distress while building your resilience. These relationships thrive on open communication, accountability, and high-level collaboration. We call them SPARC relationships: Stress-inducing, Performance-based, Accountability-centric, Result-oriented, and committed to Continuous improvement. SPARC relationships help you develop capacities that are hard to reach alone, allowing you to manage stress positively, grow sustainably, and experience deep personal and professional transformation.",
        "Meaningful Work":
            "Meaningful work is the foundation of your personal success and growth. It starts with gaining clarity about your role, responsibilities, and understanding how success if measured. When you have clear goals, a sense of purpose, and opportunities to grow, your work transforms from a series of tasks into a pathway for fulfillment, progress, and achievement.",
        'Clinical Spirituality': "Clinical Spirituality is an awareness of something greater than yourself. It is not limited to organized religion or dogmatic beliefs. Instead, it expands your sense of purpose by connecting you to your role, your team, your organization, and humanity as a whole. You can cultivate this connection by seeking clarity around your organization’s mission and values and considering how your daily work contributes to that mission. This perspective transforms your work from a set of individual tasks into a meaningful pursuit as part of something larger, enhancing your fulfillment, motivation, and overall well-being.",
        "Hobbies & Interests":
            "Hobbies and interests are vital for managing stress and building resilience. Engaging in activities outside of work fosters curiosity, independent learning, and healthy stress relief, helping you offset daily pressures and maintain balance. Pursuing hobbies and interests also increases your capacity to handle stress while working toward your goals. When you actively cultivate and protect time for these meaningful pursuits, you strengthen your ability to manage stress, sustain high performance, and live a more balanced, fulfilling life.",
        "Physical Health":
            "Maintaining good physical health is one of the most effective ways to manage stress and build resilience. Prioritizing regular physical activity helps you handle daily pressures, improves your focus and precision, and boosts your energy. Engaging in consistent health routines not only strengthens your body but also supports a longer, more vibrant life, helping you sustain high performance while living in alignment with your goals.",
    };
    return descriptions[domain as keyof typeof descriptions] || "Your assessment results provide insights into your current performance and areas for improvement.";
};

export const getActionSteps = (domain: string) => {
    const actionSteps = {
        Relationships: [
            { title: "Strengthen Workplace Relationships", description: "Invest in building friendships at work to improve collaboration, performance, accountability, and your ability to manage stress." },
            { title: "Embrace Teamwork", description: "Recognize the power of working together, understanding that united efforts often achieve more than what you can accomplish alone." },
            { title: "Create Personal Connections", description: "Look for opportunities to collaborate meaningfully with your colleagues and engage in activities that help you build strong, supportive connections." },

        ],
        "Meaningful Work": [
            { title: "Clarify Your Roles and Responsibilities", description: "Understand your responsibilities and how your contributions are measured. Seek consistent feedback to align your work with personal growth." },
            {
                title: "Align Your Work with Your Values",
                description:
                    "Reflect on what matters most to you and identify your core values. Look for ways your daily work can express these values, allowing you to find deeper purpose and meaning in your tasks and contributions.",
            },
            {
                title: "Give and Receive Feedback",
                description: "Be open to consistent feedback—both positive and corrective—and offer thoughtful feedback to others to help strengthen relationships and support shared growth.",
            },
        ],
        'Clinical Spirituality': [
            { title: "Seek Clarity of Mission", description: "Take time to understand your organization’s mission and vision, and reflect on how your daily work contributes to these larger goals." },
            { title: "Align Personal and Organizational Values", description: "Identify your personal values and consider how they align with your organization’s purpose. Look for ways your work can express what matters most to you." },
            { title: "Look for Opportunities for Meaningful Contribution", description: "Engage in opportunities to serve your community or support groups in need, whether through your organization’s initiatives or on your own, as a way to deepen your sense of purpose." },
        ],
        "Hobbies & Interests": [
            { title: "Identify What Interests You", description: "Take time to explore what hobbies and interests genuinely spark your curiosity and bring you energy." },
            { title: "Make Space for Your Hobbies", description: "Set aside consistent, protected time in your routine to engage in activities you find meaningful outside of work." },
            { title: "Connect with Others", description: "Share your interests with colleagues and build community around shared passions." },
        ],
        "Physical Health": [
            { title: "Prioritize Quality Sleep", description: "Aim for 7–9 hours of uninterrupted sleep to support your body’s repair, brain function, and overall health." },
            { title: "Maintain Balanced Nutrition", description: "Eat balanced, consistent meals and snacks throughout the day to sustain your energy and cognitive function, avoiding long periods without nourishment." },
            { title: "Engage in Regular Physical Activity", description: "Incorporate cardio, strength training, or other forms of movement into your routine to improve fitness, reduce stress, and boost your mood." },
            { title: "Stay Hydrated", description: "Drink water consistently to support your body’s essential functions, concentration, and focus." },
        ],
    };
    return actionSteps[domain as keyof typeof actionSteps] || actionSteps["Relationships"];
};

export const getReflectionQuestions = (domain: string) => {
    const questions = {
        Relationships: [
            "Have I invested time in building friendships at work that support collaboration and encourage me to grow (SPARC)?",
            "What small steps can I take to build stronger, supportive connections with others?",
            "In what ways have I contributed to my team’s success recently?",
        ],
        "Meaningful Work": [
            "Do I clearly understand my role, responsibilities, and how my contributions are measured?",
            "How does my current work align with what I value and want to contribute?",
            "Am I open to receiving and giving both positive and constructive feedback?",
        ],
        'Clinical Spirituality': [
            "Do I understand my organization’s mission and vision?",
            "When was the last time I felt connected to the purpose behind my work?",
            "Where do I see alignment between what matters to me and my organization’s purpose?",
            "What opportunities could I explore to contribute meaningfully, either through my organization or personally?",
        ],
        "Hobbies & Interests": [
            "When do I feel most engaged and alive outside of work?",
            "Have I set aside consistent time for hobbies that are meaningful to me?",
            "When was the last time I tried a new hobby or activity just for fun or learning?",
            "How might I reintroduce these activities into my life in a manageable way?",
        ],
        "Physical Health": [
            "Am I getting 7–9 hours of uninterrupted sleep most nights?",
            "How consistently am I moving my body through cardio, strength training, or other activities I enjoy?",
            "Do I eat balanced meals and snacks regularly throughout the day?",
            "Am I drinking enough water throughout the day to support my focus and physical health?",
        ],
    };
    return questions[domain as keyof typeof questions] || [];
};

// Share functionality
export const shareResults = async (result: DomainResult, selectedDomain: string) => {
    if (!result) return;

    const shareData = {
        title: `${selectedDomain} Assessment Results`,
        text: `My ${selectedDomain} assessment performance: ${result?.performanceLevel ?? ''} (${result?.category ?? ''})`,
        url: window.location.href,
    };

    if (navigator.share) {
        try {
            await navigator.share(shareData);
        } catch (error) {
            console.log("Share cancelled or failed");
        }
    } else {
        const textToCopy = `${shareData.title}\n${shareData.text}\n${shareData.url}`;
        try {
            await navigator.clipboard.writeText(textToCopy);
            alert("Results copied to clipboard!");
        } catch (error) {
            console.error("Failed to copy to clipboard:", error);
        }
    }
};

export const getExportableResults = (results: DomainResult[]) => {
    const printableContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Assessment Results</title>
        <meta charset="utf-8">
      </head>
      <body style="font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; background-color: #ffffff;">
        <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:800px; margin: 0 auto;">
          <tr>
            <td align="center" style="font-size: 14px; color: #6b7280; font-weight: 500; padding: 10px 0;">STRATA INTEL</td>
          </tr>
  
          ${results.map(result => {
            const actionSteps = getActionSteps(result.domain);
            const reflectionQuestions = getReflectionQuestions(result.domain);
  
            return `
              <tr>
                <td style="border-bottom: 3px solid #4f46e5; padding-bottom: 30px; margin-bottom: 50px;">
                  <h1 style="font-size: 24px; color: #1f2937; margin-bottom: 10px;">${result.domain} Assessment Results</h1>
                  <p style="font-size: 16px; color: #6b7280; margin: 0;">${getDomainDescription(result.domain)}</p>
                </td>
              </tr>
  
              <!-- Assessment Results Section -->
              <tr>
                <td style="background-color: #f9fafb; padding: 20px; border: 1px solid #e5e7eb; margin-top: 20px;">
                  <h2 style="font-size: 18px; color: #1f2937; border-left: 4px solid #4f46e5; padding-left: 8px;">Assessment Results</h2>
                  ${result.factors.map(factor => `
                    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom: 20px; background-color: #ffffff; border: 1px solid #e5e7eb;">
                      <tr>
                        <td style="font-weight: bold; font-size: 16px; color: #374151; padding: 10px;">${factor.name}</td>
                      </tr>
                      ${factor.questions.map(question => {
                        const bg = question.performanceLevel === "Strong" ? "#dcfce7" : question.performanceLevel === "Good" ? "#dbeafe" : "#fed7aa";
                        const color = question.performanceLevel === "Strong" ? "#166534" : question.performanceLevel === "Good" ? "#1e40af" : "#9a3412";
                        const width = question.performanceLevel === "Strong" ? "75px" : question.performanceLevel === "Good" ? "70px" : "100px";
  
                        return `
                          <tr>
                            <td style="padding: 10px 15px; border-top: 1px solid #e5e7eb; background-color: #f8fafc;">
                              <table width="100%" cellpadding="0" cellspacing="0">
                                <tr>
                                  <td style="font-size: 14px; color: #4b5563;">${question.text}</td>
                                  <td align="right" style="font-size: 12px; font-weight: bold; text-align: center; background: ${bg}; color: ${color}; width: ${width}; padding: 4px 0px; border-radius: 12px; text-transform: uppercase;">${question.performanceLevel}</td>
                                </tr>
                              </table>
                            </td>
                          </tr>
                        `;
                      }).join("")}
                    </table>
                  `).join("")}
                </td>
              </tr>
  
              <!-- Action Steps Section -->
              <tr>
                <td style="background-color: #f9fafb; padding: 20px; border: 1px solid #e5e7eb; margin-top: 20px;">
                  <h2 style="font-size: 18px; color: #1f2937; border-left: 4px solid #4f46e5; padding-left: 8px;">Action Steps</h2>
                  ${actionSteps.map((step, index) => `
                    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom: 20px; background-color: #ffffff; border: 1px solid #e5e7eb;">
                      <tr>
                        <td style="padding: 10px;">
                          <table cellpadding="0" cellspacing="0" width="100%">
                            <tr>
                              <td style="vertical-align: top;">
                                <div style="width: 24px; height: 24px; background-color: #fbbf24; color: #92400e; font-size: 12px; font-weight: bold; border-radius: 50%; text-align: center; line-height: 24px; display: inline-block; margin-right: 8px;">${index + 1}</div>
                                <span style="font-size: 16px; color: #374151; font-weight: bold;">${step.title}</span>
                              </td>
                            </tr>
                            <tr>
                              <td style="font-size: 14px; color: #6b7280; padding-top: 5px;">${step.description}</td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  `).join("")}
                </td>
              </tr>
  
              <!-- Reflection Questions Section -->
              <tr>
                <td style="background-color: #f9fafb; padding: 20px; border: 1px solid #e5e7eb; margin-top: 20px;">
                  <h2 style="font-size: 18px; color: #1f2937; border-left: 4px solid #4f46e5; padding-left: 8px;">Reflection Questions</h2>
                  ${reflectionQuestions.map((question, index) => `
                    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom: 15px; background-color: #ffffff; border: 1px solid #e5e7eb;">
                      <tr>
                        <td style="padding: 10px;">
                          <div style="display: inline-block; background-color: #10b981; color: white; font-size: 10px; font-weight: bold; width: 20px; height: 20px; border-radius: 50%; text-align: center; line-height: 20px; margin-right: 8px;">${index + 1}</div>
                          <span style="font-size: 14px; color: #4b5563;">${question}</span>
                        </td>
                      </tr>
                    </table>
                  `).join("")}
                </td>
              </tr>
            `;
          }).join("")}
  
          <tr>
            <td style="padding: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #9ca3af; text-align: center;">
              Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;
  
    return printableContent;
  };
